import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { GridApi } from 'ag-grid-community';
import React from 'react';
import { useCallback, useContext, useEffect, useReducer } from 'react';

import { buttonVariants } from '@/ui/Button/Button';
import Dropdown from '@/ui/components/Dropdown';

import { BillingReportContext, hasGrid } from '../contexts/BillingReportProvider';

// Dropdown is a legacy component with its own hand-rolled trigger styling,
// which reads inconsistently next to the toolbar's other controls (e.g.
// Alert Summary, a white/sm Button). Render its trigger with the same
// classes a real Button of that color/size resolves to, so every control in
// the row looks like it came from the same design system.
const dropdownButtonClassName = buttonVariants({
  variant: 'outline',
  color: 'neutral',
  size: 'sm'
});

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// AG Grid's date filter model (and the server's _from/_to/_after/_before
// params) both take plain YYYY-MM-DD strings -- built from local calendar
// fields rather than toISOString() so the date shown doesn't shift a day
// depending on the browser's timezone offset from UTC.
const toDateOnly = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const monthsBefore = (date: Date, months: number): Date =>
  new Date(date.getFullYear(), date.getMonth() - months, date.getDate());
const startOfYear = (date: Date): Date => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date: Date): Date => new Date(date.getFullYear(), 11, 31);
const yearsBefore = (date: Date, years: number): Date =>
  new Date(date.getFullYear() - years, date.getMonth(), date.getDate());

type SegmentFilter = Record<string, string | boolean | number>;

// Operators that take no operand: blank/notBlank (any column) and true/false
// (AG Grid's boolean-column filter).
const OPERAND_LESS_TYPES = new Set(['blank', 'notBlank', 'true', 'false']);

// The operand fields an operator can carry -- scalar filters use `filter`
// (plus `filterTo` for inRange), date filters use `dateFrom`/`dateTo` instead.
const OPERAND_KEYS = ['filter', 'filterTo', 'dateFrom', 'dateTo'] as const;

// AG Grid reconstructs the filter model it hands back from each filter's own
// state, so getFilterModel() never deep-equals a segment's literal model: it
// rewrites `filterType` to the column's actual type (e.g. a text segment on the
// manually_held_at date column comes back as `filterType: 'date'`, and the
// on_hold boolean column comes back as `filterType: 'text'`). Comparing the raw
// objects therefore never matches, which is why a clicked quick filter did not
// stay highlighted. Compare only what actually defines the filter: its
// operator (`type`), plus whichever operand fields the segment's own model
// sets -- never the `filterType`.
const filterMatches = (
  current: SegmentFilter | undefined,
  expected: SegmentFilter | undefined
): boolean => {
  if (!current || !expected) return false;
  if (current.type !== expected.type) return false;
  if (typeof expected.type === 'string' && OPERAND_LESS_TYPES.has(expected.type)) return true;

  return OPERAND_KEYS.filter((key) => expected[key] !== undefined).every(
    (key) => String(current[key]) === String(expected[key])
  );
};
export interface FilterSegment {
  name: string;
  model?: Record<string, Record<string, string | boolean | number> | undefined>;
  onClick?: () => void;
}

interface FilterButtonProps {
  tab: FilterSegment;
  toggleSegment: (tab: FilterSegment) => () => void;
  isActive: boolean;
  className?: string;
}

const FilterButton: React.FC<FilterButtonProps> = ({ tab, toggleSegment, isActive, className }) => {
  return (
    <button
      key={tab.name}
      onClick={toggleSegment(tab)}
      className={classNames(
        isActive ? 'bg-gray-200 text-gray-800' : 'text-gray-600 hover:text-gray-900',
        `rounded-md px-3 py-2 text-sm font-medium ${className}`
      )}
      aria-current={isActive ? 'page' : undefined}
      type="button">
      {tab.name}
    </button>
  );
};

export const BillingReportFilters: React.FC = () => {
  const { gridRef } = useContext(BillingReportContext);

  // Segment highlighting is derived from the grid's current filter model at
  // render time, so this component must re-render whenever that model changes --
  // including changes it triggers itself (clicking a pill) and external ones
  // (clearing a chip via FilterExplanation). We can't rely on a parent
  // re-render: BillingReportResultApp's onFilterChanged only updates the row
  // count, so a filter change that doesn't change the row count would leave a
  // pill's highlight stale. Subscribing to the grid's own filterChanged event
  // keeps the highlight correct regardless of the host page.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    let rafId = 0;
    let api: GridApi | null = null;
    const handleFilterChanged = () => forceRender();

    const attach = () => {
      const grid = gridRef.current;
      // hasGrid only rules out a null api; on mount the ref is set but the api
      // is still undefined until the grid is ready, so check it directly.
      if (grid && grid.api) {
        api = grid.api;
        api.addEventListener('filterChanged', handleFilterChanged);
      } else {
        // The grid may not be ready on mount; retry until it is.
        rafId = window.requestAnimationFrame(attach);
      }
    };

    attach();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (api && !api.isDestroyed()) {
        api.removeEventListener('filterChanged', handleFilterChanged);
      }
    };
  }, [gridRef]);

  // Quick-filter segments compose: applying a segment merges its filter keys
  // into whatever is already in the grid's filter model (e.g. Billable + Case
  // Rates, or Billable + With Holds) rather than replacing it. Segments that
  // target the same key (e.g. Case Rates/FFS on period_start_at) naturally
  // override each other, which is the desired behaviour.
  const applyFilters = (
    model: Record<string, Record<string, string | boolean | number> | undefined>
  ) => {
    if (!hasGrid(gridRef.current)) return;

    const currentFilterModel = gridRef.current.api.getFilterModel();

    gridRef.current.api.setFilterModel({ ...currentFilterModel, ...model });
  };

  // Toggling a segment off removes only that segment's keys, leaving any other
  // composed quick filters intact.
  const removeFilters = (
    model: Record<string, Record<string, string | boolean | number> | undefined>
  ) => {
    if (!hasGrid(gridRef.current)) return;

    const nextFilterModel = { ...gridRef.current.api.getFilterModel() };

    for (const key of Object.keys(model)) {
      delete nextFilterModel[key];
    }

    gridRef.current.api.setFilterModel(nextFilterModel);
  };

  const clearFilters = useCallback(() => {
    if (!hasGrid(gridRef.current)) return;

    gridRef.current.api.setFilterModel({});

    // Clear URL params
    const url = new URL(window.location.href);
    url.search = ``;
    window.history.replaceState({}, '', url.toString());
  }, [gridRef]);

  // A segment is highlighted when every filter it defines is present in the
  // grid's *current* filter model -- derived from the grid rather than local
  // click state so clearing or editing an individual filter (e.g. via
  // FilterExplanation's per-filter clear button) immediately unhighlights any
  // quick filter whose keys no longer match. Because segments compose, this is
  // a subset check (all of the segment's keys match) rather than an exact
  // whole-model match, so two active quick filters both stay highlighted.
  const isSegmentActive = (segment: FilterSegment): boolean => {
    const currentFilterModel = hasGrid(gridRef.current) ? gridRef.current.api.getFilterModel() : {};

    if (!segment.model) {
      return Object.keys(currentFilterModel).length === 0;
    }

    const modelKeys = Object.keys(segment.model);

    return modelKeys.every((key) => filterMatches(currentFilterModel[key], segment.model?.[key]));
  };

  const toggleSegment = (tab: FilterSegment) => () => {
    if (!tab.model) {
      clearFilters();
      return;
    }

    if (isSegmentActive(tab)) {
      removeFilters(tab.model);
    } else {
      applyFilters(tab.model);
    }
  };

  // Computed once per render so every "current date" segment below agrees
  // on what "today" is.
  const today = new Date();

  // Filter segments
  const filterSegments = [
    { name: 'All', onClick: clearFilters },
    {
      name: 'In Healthie',
      model: {
        cms_1500_healthie_id: {
          filterType: 'text',
          type: 'notBlank',
          filter: true
        }
      }
    },
    {
      name: 'Billable',
      model: {
        errors: {
          filterType: 'text',
          type: 'noErrors'
        },
        cms_1500_healthie_id: {
          filterType: 'text',
          type: 'blank',
          filter: true
        },
        manually_held_at: {
          filterType: 'text',
          type: 'blank',
          filter: true
        },
        // on_hold is a boolean column (cellDataType: 'boolean'); AG Grid's
        // boolean filter stores its operator as the type ('false'/'true') with
        // no operand, under filterType 'text'.
        on_hold: {
          filterType: 'text',
          type: 'false'
        },
        indicators: {
          filterType: 'text',
          type: 'not_non_billable'
        }
      }
    },
    {
      name: 'With Errors',
      model: {
        errors: {
          filterType: 'text',
          type: 'hasErrors'
        },
        cms_1500_healthie_id: {
          filterType: 'text',
          type: 'blank',
          filter: true
        }
      }
    },
    {
      name: 'With Holds',
      model: {
        // on_hold is a boolean column (cellDataType: 'boolean'); AG Grid's
        // boolean filter stores its operator as the type ('true'/'false') with
        // no operand, under filterType 'text'.
        on_hold: {
          filterType: 'text',
          type: 'true'
        }
      }
    },
    {
      name: 'This Month',
      group: 'Date',
      model: {
        service_date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toDateOnly(startOfMonth(today)),
          dateTo: toDateOnly(endOfMonth(today))
        }
      }
    },
    {
      name: 'Last Month',
      group: 'Date',
      model: {
        service_date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toDateOnly(startOfMonth(monthsBefore(today, 1))),
          dateTo: toDateOnly(endOfMonth(monthsBefore(today, 1)))
        }
      }
    },
    {
      // 3 full calendar months: this month plus the two before it.
      name: 'Past 3 Months',
      group: 'Date',
      model: {
        service_date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toDateOnly(startOfMonth(monthsBefore(today, 2))),
          dateTo: toDateOnly(endOfMonth(today))
        }
      }
    },
    {
      name: 'This Year',
      group: 'Date',
      model: {
        service_date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toDateOnly(startOfYear(today)),
          dateTo: toDateOnly(endOfYear(today))
        }
      }
    },
    {
      name: 'Last Year',
      group: 'Date',
      model: {
        service_date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toDateOnly(startOfYear(yearsBefore(today, 1))),
          dateTo: toDateOnly(endOfYear(yearsBefore(today, 1)))
        }
      }
    },
    {
      name: 'Associated',
      group: 'Other',
      model: {
        manually_associated_by: {
          filterType: 'text',
          type: 'notBlank',
          filter: true
        }
      }
    },
    {
      name: 'Manual Holds',
      model: {
        manually_held_at: {
          filterType: 'text',
          type: 'notBlank',
          filter: true
        }
      }
    },
    {
      name: 'Ignored Errors',
      model: {
        ignore_error_at: {
          filterType: 'text',
          type: 'notBlank',
          filter: true
        }
      }
    },
    {
      name: 'Case Rates',
      model: {
        period_start_at: {
          filterType: 'date',
          type: 'notBlank',
          filter: true
        }
      }
    },
    {
      name: 'FFS',
      model: {
        period_start_at: {
          filterType: 'date',
          type: 'blank',
          filter: true
        }
      }
    },
    {
      name: 'With Edits',
      group: 'Other',
      model: {
        indicators: {
          filterType: 'text',
          type: 'has_edits'
        }
      }
    },
    {
      name: 'Without Edits',
      group: 'Other',
      model: {
        indicators: {
          filterType: 'text',
          type: 'not_has_edits'
        }
      }
    },
    {
      name: 'Non-Billable',
      model: {
        indicators: {
          filterType: 'text',
          type: 'non_billable'
        }
      }
    }
  ];

  // Group segments by group
  const groupedSegments = filterSegments.reduce(
    (acc, segment) => {
      segment.group = segment.group || 'Default';

      if (!acc[segment.group]) {
        acc[segment.group] = [];
      }

      acc[segment.group].push(segment);

      return acc;
    },
    {} as Record<string, FilterSegment[]>
  );

  return (
    <nav className="flex space-x-4 items-center" aria-label="Tabs">
      {Object.entries(groupedSegments).map(([group, tabs]: [string, FilterSegment[]]) => (
        <div key={group}>
          {/* IF default segment list all filters */}
          {group === 'Default' &&
            tabs.map((tab) => (
              <FilterButton
                key={tab.name}
                tab={tab}
                toggleSegment={toggleSegment}
                isActive={isSegmentActive(tab)}
              />
            ))}
          {/* If has a group make a dropdown */}
          {group !== 'Default' && (
            <Dropdown
              // A string buttonText doubles as the dropdown's accessible name
              // (see Dropdown's aria-label fallback) -- 'Other' predates this
              // and keeps its plain icon, but a second dropdown with the same
              // icon-only label would be indistinguishable from it.
              buttonText={group === 'Other' ? <FontAwesomeIcon icon={faEllipsisVertical} /> : group}
              customButtonClassName={dropdownButtonClassName}
              items={tabs.map((tab) => (
                <FilterButton
                  tab={tab}
                  key={tab.name}
                  toggleSegment={toggleSegment}
                  isActive={isSegmentActive(tab)}
                />
              ))}
            />
          )}
        </div>
      ))}
    </nav>
  );
};

export default BillingReportFilters;
